/**
 * Authorization endpoint success response interface.
 *
 * Note that for convenience this interface does not follow
 * the specification exactly:
 *  - `expires_in` property is in milliseconds.
 *  - `expires_at` property is not part of specification.
 *
 * See: https://tools.ietf.org/html/rfc6749#section-4.2.2
 */
export interface IAuthorizationSuccess {
  access_token: string;
  expires_in: number; // modified property, milliseconds instead of seconds
  expires_at: number; // custom property, not in specification
  id_token: string;
  scope?: string;
  state: string;
  token_type: string;
  session_state?: string;
}

/**
 * Authorization endpoint error response interface.
 *
 * See: https://tools.ietf.org/html/rfc6749#section-4.2.2.1
 */
export interface IAuthorizationError {
  error: string;
  error_description?: string;
  scope?: string;
  state?: string;
}

/**
 * Creates an object from a string of query parameters.
 */
function parseQueryParametersString(queryParametersString: string): any {
  const queryParametersRegex = /([^&=]+)=([^&]*)/g;
  const queryParameters: any = {};

  let keyValuePair;
  while ((keyValuePair = queryParametersRegex.exec(queryParametersString))) {
    const key = decodeURIComponent(keyValuePair[1]);
    const value = decodeURIComponent(keyValuePair[2]);
    queryParameters[key] = value;
  }

  return queryParameters;
}

/**
 * Parses an authorization server response.
 */
export function parseOAuth2Fragment(fragment: string): any {
  return parseQueryParametersString(fragment.replace(/^#/, ''));
}

/**
 * Normalizes a parsed authorization server response.
 */
export function normalizeAuthServerResponse(authServerResponse: any): IAuthorizationSuccess | IAuthorizationError {
  if (authServerResponse.error) {
    const authError: IAuthorizationError = {
      error: authServerResponse.error,
      error_description: authServerResponse.error_description,
      state: authServerResponse.state,
    };
    return authError;
  }

  const expiresIn = parseInt(authServerResponse.expires_in, 10) * 1000;
  const authSuccess: IAuthorizationSuccess = {
    access_token: authServerResponse.access_token,
    expires_in: expiresIn,
    expires_at: Date.now() + expiresIn,
    id_token: authServerResponse.id_token,
    scope: authServerResponse.scope,
    state: authServerResponse.state,
    token_type: authServerResponse.token_type,
    session_state: authServerResponse.session_state,
  };
  return authSuccess;
}
